import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import {
  NotebookText,
  Upload,
  ArrowRight,
  LogOut,
  Gift,
  Copyright,
} from 'lucide-react';
import Logo from '../assets/logo.png';
import ProfileSettings from '../components/ProfileSettings';
import LinkedDevices from '../components/LinkedDevices';
import SubscriptionBilling from '../components/SubscriptionBilling';
import HealthGoals from '../components/HealthGoals';
import UnitsMetrics from '../components/UnitMetrics';
import SecurityPrivacy from '../components/SecurityPrivacy';
import HelpSupport from '../components/HelpSupport';
import DangerZone from '../components/DangerZone';
import InviteFriends from '../components/invite_friends';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { requestForToken } from '../firebase';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import BottomNavigation from '../components/bottom_navigation';
import FeatureHeader from '../components/FeatureHeader';
const sectionHeaderStyle = {
  fontSize: '0.85rem',
  fontWeight: '600',
  color: '#666',
  margin: '1.5rem 0 0.5rem',
};

const cardStyle = {
  background: 'var(--surface)',
  borderRadius: '16px',
  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
  padding: '12px 16px',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const iconCircleStyle = {
  background: 'var(--primary-bg)',
  borderRadius: '50%',
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const labelStyle = {
  fontSize: '0.9rem',
  fontWeight: '500',
};

const ToggleSwitch = ({ value, onChange }) => (
  <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 20 }}>
    <input
      type="checkbox"
      checked={value}
      onChange={onChange}
      style={{ opacity: 0, width: 0, height: 0 }}
    />
    <span
      style={{
        position: 'absolute',
        cursor: 'pointer',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: value ? 'var(--primary)' : '#ccc',
        borderRadius: 20,
        transition: '0.2s',
      }}
    />
    <span
      style={{
        position: 'absolute',
        content: '""',
        height: 16,
        width: 16,
        left: value ? 22 : 4,
        bottom: 2,
        backgroundColor: '#fff',
        borderRadius: '50%',
        transition: '0.2s',
      }}
    />
  </label>
);

const SettingsButton = ({
  icon,
  label,
  isToggle,
  toggleValue,
  onToggle,
  onClick,
}) => {
  const IconComponent = typeof icon === 'function' ? icon : null;
  const IconClass = typeof icon === 'string' ? icon : null;

  return (
    <div style={cardStyle} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={iconCircleStyle}>
          {IconClass ? (
            <i className={IconClass} style={{ color: 'var(--primary)', fontSize: 18 }} />
          ) : IconComponent ? (
            <IconComponent size={18} style={{ color: 'var(--primary)' }} />
          ) : null}
        </div>
        <span style={labelStyle}>{label}</span>
      </div>

      {isToggle ? (
        <ToggleSwitch value={toggleValue} onChange={onToggle} />
      ) : (
        <ArrowRight size={20} style={{ color: 'var(--primary)' }} />
      )}
    </div>
  );
};

export default function ProfilePage() {


  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState(null);
  const [isHydratingProfile, setIsHydratingProfile] = useState(false);
  const [toggles, setToggles] = useState({
    reminders: true,
    insights: false,
    push: true,
    email: false,
  });
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const pushSyncAttemptedRef = useRef(false);
  const [notificationHealth, setNotificationHealth] = useState({
    loading: true,
    tokenRegistered: false,
    tokenUpdatedAt: '',
    schedulerRunning: false,
    schedulerLastRun: '',
    schedulerRuns: 0,
    error: '',
  });
  const [pushDebug, setPushDebug] = useState({
    checking: false,
    status: '',
    lastResult: '',
  });

  const { profile, user, fetchProfile, logout } = useAuth();

  useEffect(() => {
    let mounted = true;
    const hydrateProfile = async () => {
      if (profile || typeof fetchProfile !== 'function') return;
      setIsHydratingProfile(true);
      try {
        await fetchProfile();
      } finally {
        if (mounted) setIsHydratingProfile(false);
      }
    };

    hydrateProfile();
    return () => {
      mounted = false;
    };
  }, [profile, fetchProfile]);

  // Initialize toggles from profile
  useEffect(() => {
    if (profile && profile.notifications) {
      setToggles({
        reminders: profile.notifications.reminders ?? true,
        insights: profile.notifications.insights ?? false,
        push: profile.notifications.push ?? true,
        email: profile.notifications.email ?? false,
      });
    }
  }, [profile]);

  useEffect(() => {
    if (!user?._id || !toggles.push || pushSyncAttemptedRef.current) return;

    if (!Capacitor.isNativePlatform()) {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
    }

    pushSyncAttemptedRef.current = true;
    requestForToken(user._id).catch(() => {});
  }, [user, toggles.push]);

  const refreshNotificationHealth = async () => {
    if (!user?._id) return;

    setNotificationHealth((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      const [tokenRes, schedulerRes] = await Promise.all([
        api.get('/api/notifications/token').catch((err) => {
          if (err?.response?.status === 404) {
            return { data: null };
          }
          throw err;
        }),
        api.get('/api/test/reminder-scheduler-status').catch(() => ({ data: null })),
      ]);

      const tokenData = tokenRes?.data || null;
      const status = schedulerRes?.data?.status || null;

      setNotificationHealth({
        loading: false,
        tokenRegistered: Boolean(tokenData?.token),
        tokenUpdatedAt: tokenData?.updatedAt || '',
        schedulerRunning: Boolean(status?.running),
        schedulerLastRun: status?.lastRun || '',
        schedulerRuns: Number(status?.stats?.runs || 0),
        error: '',
      });
    } catch (err) {
      setNotificationHealth((prev) => ({
        ...prev,
        loading: false,
        error: 'Unable to load notification health status',
      }));
    }
  };

  useEffect(() => {
    if (!user?._id) return;
    refreshNotificationHealth();
  }, [user?._id, toggles.reminders, toggles.push, toggles.email]);

  // Convert createdAt to readable date (guard when profile is not yet loaded)
  const readableDate = profile && profile.createdAt
    ? new Date(profile.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  // Calculate streak days (days since profile creation). Default to 0 when no profile.
  let diffDays = 0;
  if (profile && profile.createdAt) {
    const targetDate = new Date(profile.createdAt);
    const now = new Date();
    diffDays = Math.floor((now - targetDate) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) diffDays = 0;
  }

  const handleToggle = async (key) => {
    const newValue = !toggles[key];
    setToggles((prev) => ({ ...prev, [key]: newValue }));

    try {
      const res = await api.patch('/api/profile/notifications', { notifications: { [key]: newValue } });

      const serverNotifications = res?.data?.profile?.notifications;
      if (serverNotifications && typeof serverNotifications === 'object') {
        setToggles((prev) => ({
          ...prev,
          reminders: serverNotifications.reminders ?? prev.reminders,
          insights: serverNotifications.insights ?? prev.insights,
          push: serverNotifications.push ?? prev.push,
          email: serverNotifications.email ?? prev.email,
        }));
      }

      if (typeof fetchProfile === 'function') {
        await fetchProfile();
      }

      if (key === 'push') {
        if (newValue) {
          await requestForToken(user?._id);
        } else {
          await api.delete('/api/notifications/token');
        }
      }

      await refreshNotificationHealth();
    } catch (err) {
      console.error('Failed to update notification preference:', err);
      // Revert on error
      setToggles((prev) => ({ ...prev, [key]: !newValue }));
      toast.error('Failed to update notification setting');
    }
  };

  const checkPushDebugStatus = async () => {
    setPushDebug((prev) => ({ ...prev, checking: true, status: '', lastResult: '' }));

    try {
      if (Capacitor.isNativePlatform()) {
        const permission = await PushNotifications.checkPermissions();
        const platform = Capacitor.getPlatform();
        setPushDebug({
          checking: false,
          status: `Platform: ${platform} | Permission: ${permission?.receive || 'unknown'}`,
          lastResult: notificationHealth.tokenRegistered ? 'Token is already registered' : 'Token not registered yet',
        });
        return;
      }

      const browserPermission = typeof window !== 'undefined' && 'Notification' in window
        ? Notification.permission
        : 'unsupported';
      setPushDebug({
        checking: false,
        status: `Platform: web | Permission: ${browserPermission}`,
        lastResult: notificationHealth.tokenRegistered ? 'Token is already registered' : 'Token not registered yet',
      });
    } catch (err) {
      setPushDebug({
        checking: false,
        status: 'Failed to check push status',
        lastResult: err?.message || 'Unknown error',
      });
    }
  };

  const retryPushRegistration = async () => {
    if (!user?._id) {
      toast.error('No signed-in user found for push registration');
      return;
    }

    setPushDebug((prev) => ({ ...prev, checking: true, status: 'Retrying push registration...', lastResult: '' }));

    try {
      const result = await requestForToken(user._id);
      await refreshNotificationHealth();
      setPushDebug({
        checking: false,
        status: result?.ok ? 'Push registration requested successfully' : 'Push registration did not complete',
        lastResult: result?.reason || (result?.ok ? 'OK' : 'Unknown result'),
      });
    } catch (err) {
      setPushDebug({
        checking: false,
        status: 'Push registration retry failed',
        lastResult: err?.message || 'Unknown error',
      });
    }
  };

  const renderSubView = () => {
    switch (currentView) {
      case 'Profile Settings': return <ProfileSettings onBack={() => setCurrentView(null)} />;
      case 'invite': return <InviteFriends onBack={() => setCurrentView(null)} />;
      case 'Linked Devices': return <LinkedDevices onBack={() => setCurrentView(null)} />;
      case 'Subscription and Billing': return <SubscriptionBilling onBack={() => setCurrentView(null)} />;
      case 'Health Goals': return <HealthGoals onBack={() => setCurrentView(null)} />;
      case 'Units & Metrics': return <UnitsMetrics onBack={() => setCurrentView(null)} />;
      case 'Change Password':
      case 'Data Sharing':
      case 'Clear and Reset':
      case 'Change Passcode':
        return <SecurityPrivacy onBack={() => setCurrentView(null)} />;
      case 'Help & Support':
      case 'FAQs':
      case 'Live Chat':
      case 'Feature Request':
      case 'What’s New':
        return <HelpSupport onBack={() => setCurrentView(null)} />;
      case 'Delete Account': return <DangerZone onBack={() => setCurrentView(null)} />;
      default: return null;
    }
  };

  // CLOUDINARY UPLOAD
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "profile_preset"); // your unsigned preset name

    const res = await fetch(`https://api.cloudinary.com/v1_1/dp09q5por/image/upload`, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();
    return data.secure_url;
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be 5MB or less');
      e.target.value = '';
      return;
    }

    setLoadingAvatar(true);

    try {
      const url = await uploadToCloudinary(file);
      if (!url) throw new Error('Cloudinary upload did not return a URL');

      // Send to backend - only send the avatar field (backend expects { avatar })
      await api.patch('/api/profile/avatar', { avatar: url });

      // Refresh profile in context so UI updates
      if (typeof fetchProfile === 'function') {
        await fetchProfile();
      }
      toast.success('Profile photo updated');
    
    } catch (err) {
      console.error(err);
      toast.error('Avatar upload failed. Please try again.');
    } finally {
      setLoadingAvatar(false);
      e.target.value = '';
    }
  };

    const handleLogout = async () => {
    try {
       

       await logout();


            // Clear user context
   

      // Feedback toast
      toast.success('Signed out successfully 👋', {
        position: 'top-center',
        autoClose: 2000,
      });

      navigate('/signin', { replace: true });


    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out. Please try again.');
    }
  };


  return (
    <>
      {currentView === null ? (
        <div className='container' style={{  margin: '0 auto',  fontFamily: 'sans-serif' }}>
          {/* Back Button */}
          <FeatureHeader title = "Profile" subtitle = "View and Access personal information" onBack={() => navigate(-1)} />

          {/* Hero Section */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <NotebookText size={22} style={{ color: 'var(--primary)', cursor: 'pointer' }} onClick={() => navigate('/profile/full')} />
            <div style={{ position: 'relative' }}>
              <>
                {profile?.avatar ? (
                  <img
                    src={profile.avatar}
                    alt={profile.fullName || 'Profile'}
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #7F5AF0, #9F7AEA)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: '600',
                      fontSize: '1rem',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    }}
                  >
                    {profile?.fullName ? profile.fullName.slice(0, 2).toUpperCase() : 'F'}
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleAvatarChange}
                />

                <button
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    background: '#fff',
                    border: '1px solid var(--bg)',
                    borderRadius: '50%',
                    padding: 4,
                    cursor: 'pointer'
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {loadingAvatar ? "..." : <Upload size={16} style={{ color: 'var(--primary)' }} />}
                </button>
              </>
            </div>
            <div style={{ width: 22, height: 22 }} />
          </div>

          {/* Profile Info Section */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Qureo</h1>
            <p style={{ fontSize: '0.75rem', color: '#888' }}>{readableDate}</p>
            <h2 style={{ fontSize: '1rem', fontWeight: '600', marginTop: 4 }}>
              {profile?.fullName || user?.fullName || 'My Profile'}
            </h2>
            {isHydratingProfile && (
              <p style={{ marginTop: 6, fontSize: '0.72rem', color: '#888' }}>Loading profile...</p>
            )}
          </div>

          {/* Invite Friends Card */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <div>
              <h3 style={{ fontSize: '0.9rem', fontWeight: '600' }}>Invite Friends To Get Healthier</h3>
              <p style={{ fontSize: '0.75rem', color: '#666' }}>...and get coupons!</p>
              <button
                style={{
                  marginTop: 8,
                  padding: '6px 12px',
                  fontSize: '0.75rem',
                  background: 'var(--primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                }}
                onClick={() => setCurrentView("invite")}
              >
                Invite Friends
              </button>
            </div>
            <div style={{ ...iconCircleStyle, width: 48, height: 48 }}>
              <Gift size={24} style={{ color: 'var(--primary)' }} />
            </div>
          </div>

          {/* Streak */}
          <div style={{ ...cardStyle, flexDirection: 'column', alignItems: 'flex-start' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: '600' }}>
              Total Streak: <span style={{ color: 'var(--primary)' }}>{diffDays}</span>
            </h3>
            <p style={{ fontSize: '0.75rem', color: '#666', marginTop: 4 }}>
              You're on fire! Keep using the app to get benefits.
            </p>
          </div>

          {/* Settings Sections */}
          <h2 style={sectionHeaderStyle}>General Settings</h2>
          {[{ icon: "fi fi-tr-circle-user", label: 'Profile Settings' }, { icon: "fi fi-tr-watch-smart", label: 'Linked Devices' }, { icon: "fi fi-ts-sack-dollar", label: 'Subscription and Billing' }, { icon: "fi fi-ts-stethoscope", label: 'Health Goals' }, { icon: "fi fi-ts-ruler-combined", label: 'Units & Metrics' }]
            .map(({ icon, label }) => (
              <SettingsButton key={label} icon={icon} label={label} onClick={() => setCurrentView(label)} />
            ))}

          <h2 style={sectionHeaderStyle}>Notifications</h2>
          {[{ icon: "fi fi-ts-bell-ring", label: 'Reminders', key: 'reminders' }, { icon: "fi fi-ts-cowbell-more", label: 'Insights', key: 'insights' }, { icon: "fi fi-ts-bell-concierge", label: 'Push Notification', key: 'push' }, { icon: "fi fi-tr-at", label: 'Email Notification', key: 'email' }]
            .map(({ icon, label, key }) => (
              <SettingsButton key={label} icon={icon} label={label} isToggle toggleValue={toggles[key]} onToggle={() => handleToggle(key)} />
            ))}

          <div style={{ ...cardStyle, flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
            <div style={{ fontSize: '0.88rem', fontWeight: '600' }}>Notification Health</div>

            {notificationHealth.loading ? (
              <div style={{ fontSize: '0.78rem', color: '#666' }}>Checking notification systems...</div>
            ) : (
              <>
                <div style={{ fontSize: '0.78rem', color: '#444' }}>
                  System Reminders: <strong>{toggles.reminders ? 'Enabled' : 'Disabled'}</strong>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#444' }}>
                  Push: <strong>{toggles.push ? 'Enabled' : 'Disabled'}</strong> • Device Token: <strong>{notificationHealth.tokenRegistered ? 'Registered' : 'Missing'}</strong>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#444' }}>
                  Email: <strong>{toggles.email ? 'Enabled' : 'Disabled'}</strong>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#444' }}>
                  Reminder Scheduler: <strong>{notificationHealth.schedulerRunning ? 'Running' : 'Not Running'}</strong> (runs: {notificationHealth.schedulerRuns})
                </div>
                {notificationHealth.schedulerLastRun && (
                  <div style={{ fontSize: '0.76rem', color: '#666' }}>
                    Last scheduler run: {new Date(notificationHealth.schedulerLastRun).toLocaleString()}
                  </div>
                )}
                {notificationHealth.tokenUpdatedAt && (
                  <div style={{ fontSize: '0.76rem', color: '#666' }}>
                    Token last updated: {new Date(notificationHealth.tokenUpdatedAt).toLocaleString()}
                  </div>
                )}
                {toggles.push && !notificationHealth.tokenRegistered && (
                  <div style={{ fontSize: '0.76rem', color: '#b42318' }}>
                    Push is enabled but no device token is registered. Re-enable push or allow browser notifications.
                  </div>
                )}
              </>
            )}

            {notificationHealth.error && (
              <div style={{ fontSize: '0.76rem', color: '#b42318' }}>{notificationHealth.error}</div>
            )}

            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={checkPushDebugStatus}
                disabled={pushDebug.checking}
                style={{
                  border: '1px solid #d0d5dd',
                  background: '#fff',
                  color: '#344054',
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: '0.74rem',
                  cursor: pushDebug.checking ? 'not-allowed' : 'pointer',
                }}
              >
                Check Push Status
              </button>
              <button
                type="button"
                onClick={retryPushRegistration}
                disabled={pushDebug.checking}
                style={{
                  border: 'none',
                  background: 'var(--primary)',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: '0.74rem',
                  cursor: pushDebug.checking ? 'not-allowed' : 'pointer',
                }}
              >
                Retry Push Registration
              </button>
            </div>

            {(pushDebug.status || pushDebug.lastResult) && (
              <div style={{ marginTop: 6, fontSize: '0.76rem', color: '#475467' }}>
                <div>{pushDebug.status}</div>
                {pushDebug.lastResult && <div>Result: {pushDebug.lastResult}</div>}
              </div>
            )}
          </div>

          <h2 style={sectionHeaderStyle}>Security & Privacy</h2>
          <SettingsButton
            icon={"fi fi-tr-shield-check"}
            label="Terms & Privacy Policy"
            onClick={() => navigate('/terms-policy')}
          />
          {[{ icon: "fi fi-tr-password-smartphone", label: 'Change Password' }, { icon: "fi fi-ts-cloud-share", label: 'Data Sharing' }, { icon: "fi fi-tr-minus-circle", label: 'Clear and Reset' }, { icon: "fi fi-tr-otp", label: 'Change Passcode' }]
            .map(({ icon, label }) => (
              <SettingsButton key={label} icon={icon} label={label} onClick={() => setCurrentView(label)} />
            ))}

          <h2 style={sectionHeaderStyle}>Help & Support</h2>
          {[{ icon: "fi fi-tr-meeting", label: 'Help & Support' }]
            .map(({ icon, label }) => (
              <SettingsButton key={label} icon={icon} label={label} onClick={() => setCurrentView(label)} />
            ))}

          <h2 style={sectionHeaderStyle}>Danger Zone</h2>
          <SettingsButton icon={"fi fi-tr-delete-user"} label="Delete Account" onClick={() => setCurrentView('Delete Account')} />

          <div onClick={handleLogout} style={{ textAlign: 'center', marginTop: 24, color: 'red', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
            <LogOut  size={20} />
            <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Sign Out</span>
          </div>

          {/* Footer */}
          {/* Footer */}
<div
  style={{
    textAlign: "center",
    marginTop: 4,
    fontSize: "0.75rem",
    color: "#999",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  }}
>

  <div style = {{display : "flex", width : "100%", justifyContent : "center"}}>
  <img
    src={Logo}
    alt="Logo"
    style={{
      width: 70,
      height: 80,
      objectFit: "contain",
      marginBottom: 4,
    }}
  />
</div>
  <div>Qureo</div>

  <div>
    <button
      type="button"
      onClick={() => navigate("https://qureohealth.com/terms-of-use")}
      style={{
        border: "none",
        background: "transparent",
        color: "#777",
        fontSize: "0.74rem",
        textDecoration: "underline",
        cursor: "pointer",
        marginTop: 4,
      }}
    >
      Terms & Privacy Policy
    </button>
  </div>

  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 4,
    }}
  >
    <Copyright size={14} />
    <span>All rights reserved, 2025</span>
  </div>
</div>
          {/* Render Subview */}
          {renderSubView()}
        </div>
      ) : (
        renderSubView()
      )}

      <ToastContainer />
      {currentView === null && <BottomNavigation />}
    </>
  );
}
